/*
 * Copyright (c) Nexign, JSC, 2022-2024.
 */
/**
 *
 *
 * @author Julia Ignatieva
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @version 1.28.0
 * @since 1.19.0
 */

import { IPageHistoryInquiryTranslation } from '@modules/internationalization/types';
import {
    TableCellDate,
    TableCellStatus,
    TableCellText,
    TableFilterMultiSelect,
    TableCellLinkTooltipText,
    TTableColumnConfig,
    useTableCellAccount,
} from '@modules/table';
import TableFilterRangeDatePicker from '@modules/table/components/Filter/TableFilterRangeDatePicker';
import TableHeader from '@modules/table/components/TableHeader';

import { EFormatData } from '../../PageDashboard/types/widgetTypes';
import { ITableDefaultInquiry } from '../types';
import { getInquiryStatusIcon } from '../utils/getInquiryStatusIcon';
import { useTableOrderCellAction } from './Cell/useTableInquiryCellLink';

export const TableInquiryColumns = ({
    table,
}: IPageHistoryInquiryTranslation): TTableColumnConfig<ITableDefaultInquiry> => {
    const useInquiryCell = ({ id, branch }: ITableDefaultInquiry) => useTableOrderCellAction(id, branch);
    const useAccountCell = ({ account }: ITableDefaultInquiry) =>
        useTableCellAccount(account.id.toString(), account.number);

    return {
        /* Номер */
        id: {
            accessor: 'id',
            Header: (
                <TableHeader label={table.id.header} name="id">
                    <TableFilterMultiSelect name="id" />
                </TableHeader>
            ),
            ...TableCellLinkTooltipText<ITableDefaultInquiry>(useInquiryCell),
        },
        /* Лицевой счет */
        account: {
            accessor: 'account',
            Header: (
                <TableHeader label={table.account.header} name="account">
                    <TableFilterMultiSelect name="account" />
                </TableHeader>
            ),
            ...TableCellLinkTooltipText<ITableDefaultInquiry>(useAccountCell),
        },
        /* Номер / ID услуги */
        subscriberNumber: {
            accessor: 'subscriberNumber',
            Header: (
                <TableHeader label={table.subscriberNumber.header} name="subscriberNumber">
                    <TableFilterMultiSelect name="subscriberNumber" />
                </TableHeader>
            ),
            ...TableCellText<ITableDefaultInquiry>(({ subscriberNumber }) => subscriberNumber),
        },
        /* Дата */
        createDate: {
            accessor: 'createDate',
            Header: (
                <TableHeader label={table.createDate.header} name="createDate">
                    <TableFilterRangeDatePicker name="createDate" />
                </TableHeader>
            ),
            ...TableCellDate<ITableDefaultInquiry>(({ createDate }) => ({
                date: createDate,
                format: EFormatData.FullData,
            })),
            rowCount: 2,
        },
        /* Полное время окончания (отключено BBPORTAL-14497) 
        closeDate: {
            accessor: 'closeDate',
            Header: (
                <TableHeader label={table.closeDate.header} name="closeDate">
                    <TableFilterRangeDatePicker name="closeDate" />
                </TableHeader>
            ),
            ...TableCellDate<ITableDefaultInquiry>(({ closeDate }) => ({
                date: closeDate,
                format: EFormatData.FullData,
            })),
            rowCount: 2,
        }, */
        /* Обновлено */
        updateDate: {
            accessor: 'updateDate',
            Header: (
                <TableHeader label={table.updateDate.header} name="updateDate">
                    <TableFilterRangeDatePicker name="updateDate" />
                </TableHeader>
            ),
            ...TableCellDate<ITableDefaultInquiry>(({ updateDate }) => ({
                date: updateDate,
                format: EFormatData.FullData,
            })),
            rowCount: 2,
        },
        /* Описание */
        description: {
            accessor: 'description',
            Header: (
                <TableHeader label={table.description.header} name="description">
                    <TableFilterMultiSelect name="description" tooltipMaxHeight="300px" tooltipOverflow="auto" />
                </TableHeader>
            ),
            ...TableCellText<ITableDefaultInquiry>(({ description }) => description),
        },
        /* Тип обращения */
        topic: {
            accessor: 'topic',
            Header: (
                <TableHeader label={table.topic.header} name="topic">
                    <TableFilterMultiSelect name="topic" />
                </TableHeader>
            ),
            ...TableCellText<ITableDefaultInquiry>(({ topic }) => topic),
        },
        /* Статус */
        status: {
            accessor: 'status',
            Header: (
                <TableHeader label={table.status.header} name="status">
                    <TableFilterMultiSelect name="status" />
                </TableHeader>
            ),
            ...TableCellStatus<ITableDefaultInquiry>(({ status, statusId }) => ({
                icon: getInquiryStatusIcon(statusId),
                status,
            })),
        },
    };
};
